import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Button } from '@worldcoo/ui/components/button';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { toast } from '@/lib/toast';
import { useAppForm } from '@/lib/use-app-form';
import { NgoLanguageSelect } from './-components/ngo-language-select';
import { useEffect, useState } from 'react';
import { navigateWithViewTransition } from '@/lib/view-transition-utils';
import * as v from 'valibot';

export const Route = createFileRoute('/ngos/impersonate')({
	validateSearch: v.object({
		redirect: v.fallback(v.optional(v.string()), undefined),
	}),
	component: ImpersonatePage,
});

function ImpersonatePage() {
	const { t } = useLingui();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { redirect: redirectPath } = Route.useSearch();

	const adminSession = useQuery(trpc.auth.validateAdminSession.queryOptions());

	// Redirect to login if not authenticated
	useEffect(() => {
		if (adminSession.data && !adminSession.data.authenticated) {
			navigate({ to: '/ngos/login' });
		}
	}, [adminSession.data, navigate]);

	const ngoList = useQuery({
		...trpc.auth.listAllNgos.queryOptions(),
		enabled: adminSession.data?.authenticated === true,
	});

	const [searchQuery, setSearchQuery] = useState('');

	const { mutate: impersonate, isPending } = useMutation({
		...trpc.auth.impersonate.mutationOptions(),
		onSuccess: (result) => {
			if (result.success) {
				queryClient.removeQueries({ queryKey: trpc.ngo.pathKey() });
				if (redirectPath) {
					// redirectPath is a path captured from window.location (e.g. "/projects")
					// Use href assignment so the browser handles path + search + hash natively
					window.location.href = redirectPath;
				} else {
					navigateWithViewTransition(
						() => navigate({ to: '/ngos/projects' }),
						() => !!document.querySelector('[data-slot="sidebar"]'),
					);
				}
			} else if (result.error === 'not_found') {
				toast.error(t`No NGO found with that email`);
			} else {
				toast.error(t`Unauthorized`);
			}
		},
		onError: () => {
			toast.error(t`Something went wrong. Please try again.`);
		},
	});

	const form = useAppForm({
		defaultValues: { email: '' },
		validators: {
			onSubmit: v.object({
				email: v.pipe(v.string(), v.minLength(1)),
			}),
		},
		onSubmit: async ({ value }) => {
			if (!value.email.trim()) return;
			impersonate({ email: value.email.trim() });
		},
	});

	const filteredNgos =
		ngoList.data?.success && ngoList.data.ngos
			? ngoList.data.ngos.filter((ngo) => {
					if (!searchQuery) return true;
					const q = searchQuery.toLowerCase();
					return (
						ngo.name?.toLowerCase().includes(q) ||
						ngo.email?.toLowerCase().includes(q) ||
						ngo.id?.toLowerCase().includes(q)
					);
				})
			: [];

	return (
		<div data-page="impersonate" className="relative flex min-h-screen bg-white">
			<div className="absolute bottom-6 left-6 px-12 py-6">
				<NgoLanguageSelect />
			</div>

			<div className="flex flex-1 flex-col p-20">
				<WorldcooLogo accentClassName="text-[#f28e41]" className="h-20 w-auto self-start" />

				<div className="mt-12 flex flex-1 flex-col">
					<div className="flex max-w-150 flex-col gap-6">
						<div className="flex flex-col gap-2 text-[#35433f]">
							<h1 className="text-[32px] leading-9.5 font-medium tracking-[-0.2px]">{t`Impersonate NGO`}</h1>
							<p className="text-base leading-normal text-[#6b7280]">
								{t`Logged in as`}{' '}
								<span className="font-medium text-[#35433f]">
									{adminSession.data?.authenticated ? adminSession.data.adminEmail : '...'}
								</span>
							</p>
							<p className="text-sm leading-normal text-[#6b7280]">
								{t`Enter an NGO email or select one from the list to impersonate their session.`}
							</p>
						</div>

						{/* Manual email entry */}
						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
							className="flex flex-col gap-4"
						>
							<form.AppField
								name="email"
								children={(field) => (
									<field.TextField
										label={t`NGO Email`}
										placeholder={t`Enter the NGO contact email`}
										className="[&_input]:h-10"
									/>
								)}
							/>
							<Button type="submit" className="h-10 w-fit" disabled={isPending}>
								{isPending ? t`Impersonating...` : t`Impersonate`}
							</Button>
						</form>

						{/* NGO list */}
						<div className="flex flex-col gap-3">
							<h2 className="text-lg font-medium text-[#35433f]">{t`Or select an NGO`}</h2>

							<input
								type="text"
								placeholder={t`Search by name, email, or ID...`}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="h-10 w-full rounded-md border border-[#d1d5db] px-3 text-sm outline-none focus:border-[#f28e41] focus:ring-1 focus:ring-[#f28e41]"
							/>

							<div className="max-h-100 min-h-60 overflow-y-auto rounded-lg border border-[#e5e7eb]">
								{ngoList.isPending && (
									<div className="flex flex-col">
										{Array.from({ length: 5 }).map((_, i) => (
											<div
												// oxlint-disable-next-line react/no-array-index-key
												key={`skeleton-${i}`}
												className="flex items-center gap-3 border-b border-[#f3f4f6] px-4 py-3 last:border-b-0"
											>
												<div className="size-8 shrink-0 animate-pulse rounded-lg bg-[#e5e7eb]" />
												<div className="flex flex-1 flex-col gap-1.5">
													<div className="h-3.5 w-32 animate-pulse rounded bg-[#e5e7eb]" />
													<div className="h-3 w-48 animate-pulse rounded bg-[#e5e7eb]" />
												</div>
											</div>
										))}
									</div>
								)}
								{!ngoList.isPending && filteredNgos.length === 0 && (
									<p className="p-4 text-center text-sm text-[#6b7280]">{t`No NGOs found`}</p>
								)}
								{filteredNgos.map((ngo) => (
									<NgoRow
										key={ngo.id}
										ngo={ngo}
										isPending={isPending}
										onImpersonate={(email) => impersonate({ email })}
									/>
								))}
							</div>
						</div>

						<button
							type="button"
							onClick={() => navigate({ to: '/ngos/login' })}
							className="mt-2 w-fit text-sm text-[#6b7280] underline hover:text-[#35433f]"
						>
							{t`Back to login`}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function NgoRow(props: {
	ngo: { id: string; name: string; email: string; type: 'draft' | 'active' };
	isPending: boolean;
	onImpersonate: (email: string) => void;
}) {
	const { t } = useLingui();

	return (
		<button
			type="button"
			disabled={props.isPending}
			onClick={() => props.onImpersonate(props.ngo.email)}
			className="flex w-full items-center gap-3 border-b border-[#f3f4f6] px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[#fff7ed] disabled:opacity-50"
		>
			<div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-[#e5e7eb] bg-white text-xs font-bold text-[#f28e41]">
				{props.ngo.name?.charAt(0)?.toUpperCase() ?? 'N'}
			</div>
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="truncate text-sm font-medium text-[#1d1d1b]">{props.ngo.name || t`Unnamed NGO`}</span>
				<span className="truncate text-xs text-[#6b7280]">{props.ngo.email}</span>
			</div>
			{props.ngo.type === 'draft' && (
				<span className="shrink-0 rounded-full bg-[#fef3c7] px-2 py-0.5 text-xs font-medium text-[#92400e]">{t`Draft`}</span>
			)}
		</button>
	);
}
